// admin-adverts.js - Admin "Adverts" subtab: curate the promotional cards that
// appear on the GDBS dashboard (features, plugins, modules, services). List +
// upsert form + live preview + featured toggle + delete. Talks to the admin-only
// /api/admin/adverts endpoints. Mirrors admin-subscribers.js / admin-shares.js:
// a Bearer-token fetch helper, lazy load on first tab open, and render into a
// fixed container (#adverts-admin). The generic .atab handler in admin-tickets.js
// shows/hides #apanel-adverts; this module only fills its inner container.

import { getUser } from './auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

// Bearer-token fetch idiom shared with admin-subscribers.js / email/studio.js.
async function api(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  if (token()) headers['Authorization'] = 'Bearer ' + token();
  const res = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${method} ${path}: ${res.status}`);
  return json;
}

function esc(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
}
function isAdmin() { try { return getUser()?.role === 'admin'; } catch { return false; } }
function fmtDate(iso) { if (!iso) return '-'; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } }

// datetime-local <-> ISO helpers. The input wants "YYYY-MM-DDTHH:mm" in LOCAL
// time; the API stores ISO (UTC). Convert both ways, tolerating empty values so
// an optional start/end window can be left blank (null on the wire).
function isoToLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}
function localInputToIso(val) {
  if (!val) return null;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

// The four advert kinds the dashboard understands. Kept here (not fetched) so the
// select is stable even if the list endpoint is empty.
const KINDS = ['feature', 'plugin', 'module', 'service'];
// Default accent when none is chosen - the GDBS indigo used elsewhere in admin.
const DEFAULT_ACCENT = '#6366f1';

let rows = [];          // adverts from the server
let domains = [];       // distinct catalog verticals for the domain <select>
let editing = null;     // the advert currently loaded into the form (null = new)
let loaded = false;
let containerEl = null;  // resolved once in init / first load

function root() {
  return containerEl || document.getElementById('adverts-admin');
}

// Pull the DISTINCT `vertical` values out of the public plugin catalog so the
// domain select offers the same taxonomy the dashboard groups plugins by. A
// blank "General" option (value "") is prepended for site-wide adverts. Failure
// is non-fatal: a broken catalog fetch just leaves the General-only select.
async function loadDomains() {
  try {
    const res = await fetch('/pages/catalog.json', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) return;
    const cat = await res.json();
    const set = new Set();
    (Array.isArray(cat) ? cat : []).forEach(e => {
      const v = e && e.vertical ? String(e.vertical).trim() : '';
      // Exclude non-content catalog verticals (tiers/modules/add-ons) from the domain list.
      if (v && !['Subscription Tier', 'Research Engine', 'Add-on Product'].includes(v)) set.add(v);
    });
    domains = Array.from(set).sort((a, b) => a.localeCompare(b));
  } catch { /* leave domains as-is; General still works */ }
}

// Live preview card - what the dashboard advert will look like. Reads the form
// inputs directly so the admin sees edits as they type. Icon is a FontAwesome
// class stored without the style prefix (e.g. "fa-star"), so we render it as
// `fas <icon>`. Accent drives the left border and the CTA button.
function renderPreview() {
  const el = root(); if (!el) return;
  const box = el.querySelector('#adv-preview'); if (!box) return;
  const g = id => (el.querySelector('#' + id)?.value || '').trim();
  const title = g('adv-title') || 'Advert title';
  const blurb = g('adv-blurb') || 'A short blurb describing what this advert promotes.';
  const iconRaw = g('adv-icon');
  const icon = iconRaw || 'fa-bullhorn';
  const accent = g('adv-accent') || DEFAULT_ACCENT;
  const ctaLabel = g('adv-ctaLabel') || 'Learn more';
  const ctaUrl = g('adv-ctaUrl') || '#';
  const detailUrl = g('adv-detailUrl');
  const detailBtn = detailUrl
    ? `<a href="${esc(detailUrl)}" onclick="return false;" style="display:inline-block;margin-left:8px;background:transparent;color:${esc(accent)};text-decoration:none;font-size:12px;font-weight:600;padding:6px 12px;border:1px solid ${esc(accent)};border-radius:6px;">Details</a>`
    : '';
  box.innerHTML = `
    <div style="display:flex;gap:14px;align-items:flex-start;max-width:420px;background:#ffffff;border:1px solid #e2e8f0;border-left:4px solid ${esc(accent)};border-radius:8px;padding:16px 18px;box-shadow:0 1px 3px rgba(15,23,42,0.06);">
      <div style="flex:0 0 auto;width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:${esc(accent)}1a;color:${esc(accent)};font-size:18px;">
        <i class="fas ${esc(icon)}"></i>
      </div>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:700;color:#334155;font-size:15px;margin-bottom:4px;">${esc(title)}</div>
        <div style="color:#64748b;font-size:13px;line-height:1.45;margin-bottom:10px;">${esc(blurb)}</div>
        <a href="${esc(ctaUrl)}" onclick="return false;" style="display:inline-block;background:${esc(accent)};color:#ffffff;text-decoration:none;font-size:12px;font-weight:600;padding:6px 14px;border-radius:6px;">${esc(ctaLabel)}</a>${detailBtn}
      </div>
    </div>`;
}

function domainOptions(selected) {
  const opts = ['<option value="">General</option>'];
  domains.forEach(d => {
    opts.push(`<option value="${esc(d)}"${d === selected ? ' selected' : ''}>${esc(d)}</option>`);
  });
  // If an advert carries a domain no longer present in the catalog, keep it so an
  // edit does not silently drop it.
  if (selected && !domains.includes(selected)) {
    opts.push(`<option value="${esc(selected)}" selected>${esc(selected)} (legacy)</option>`);
  }
  return opts.join('');
}

function kindOptions(selected) {
  return KINDS.map(k =>
    `<option value="${k}"${k === (selected || 'feature') ? ' selected' : ''}>${k.charAt(0).toUpperCase() + k.slice(1)}</option>`
  ).join('');
}

function render() {
  const el = root(); if (!el) return;
  const e = editing || {};

  const bodyRows = rows.map(a => `
    <tr>
      <td style="color:#334155;font-weight:600;"><i class="fas ${esc(a.icon || 'fa-bullhorn')}" style="color:${esc(a.accent || DEFAULT_ACCENT)};margin-right:6px;"></i>${esc(a.title)}</td>
      <td style="color:#64748b;">${esc(a.domain) || '<span style="color:#94a3b8;">General</span>'}</td>
      <td style="color:#64748b;">${esc(a.kind || 'feature')}</td>
      <td style="text-align:center;"><input type="checkbox" class="adv-featured" data-id="${esc(a.id)}"${a.featured ? ' checked' : ''} title="Show as featured on the dashboard"></td>
      <td style="text-align:right;color:#64748b;">${Number(a.order) || 0}</td>
      <td style="color:#94a3b8;font-size:11px;">${fmtDate(a.createdAt)}</td>
      <td style="white-space:nowrap;">
        <button class="btn btn-sm adv-edit" data-id="${esc(a.id)}" title="Edit"><i class="fas fa-pen"></i></button>
        <button class="btn btn-sm adv-del" data-id="${esc(a.id)}" title="Delete" style="color:#dc2626;"><i class="fas fa-trash"></i></button>
      </td>
    </tr>`).join('');

  el.innerHTML = `
    <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,380px);gap:22px;align-items:start;">
      <div style="min-width:0;">
        <div style="overflow:auto;">
          <table class="admin-table" style="width:100%;">
            <thead><tr>
              <th>Title</th><th>Domain</th><th>Kind</th><th style="text-align:center;">Featured</th>
              <th style="text-align:right;">Order</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>${bodyRows || `<tr><td colspan="7" style="color:#94a3b8;padding:16px;">No adverts yet. Create one with the form.</td></tr>`}</tbody>
          </table>
        </div>
      </div>

      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:18px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
          <h3 style="margin:0;color:#334155;font-size:15px;">${editing ? 'Edit advert' : 'New advert'}</h3>
          ${editing ? '<button id="adv-cancel" class="btn btn-sm">Cancel</button>' : ''}
        </div>
        <form id="adv-form" style="display:flex;flex-direction:column;gap:10px;">
          <label style="font-size:12px;color:#475569;">Title
            <input id="adv-title" type="text" required value="${esc(e.title)}" placeholder="Try the PROTAC audit plugin" style="width:100%;margin-top:3px;">
          </label>
          <label style="font-size:12px;color:#475569;">Blurb
            <textarea id="adv-blurb" rows="2" placeholder="One line on what it does and why it matters." style="width:100%;margin-top:3px;resize:vertical;">${esc(e.blurb)}</textarea>
          </label>
          <div style="display:flex;gap:10px;">
            <label style="font-size:12px;color:#475569;flex:1;">Icon (FontAwesome)
              <input id="adv-icon" type="text" value="${esc(e.icon)}" placeholder="fa-star" style="width:100%;margin-top:3px;">
            </label>
            <label style="font-size:12px;color:#475569;">Accent
              <input id="adv-accent" type="color" value="${esc(e.accent) || DEFAULT_ACCENT}" style="margin-top:3px;height:34px;width:48px;padding:2px;">
            </label>
          </div>
          <div style="display:flex;gap:10px;">
            <label style="font-size:12px;color:#475569;flex:1;">CTA label
              <input id="adv-ctaLabel" type="text" value="${esc(e.ctaLabel)}" placeholder="Open plugin" style="width:100%;margin-top:3px;">
            </label>
            <label style="font-size:12px;color:#475569;flex:2;">Open link (CTA URL)
              <input id="adv-ctaUrl" type="text" value="${esc(e.ctaUrl)}" placeholder="#open:pro:legal_k12_device_policy" style="width:100%;margin-top:3px;">
              <span style="font-size:10px;color:#94a3b8;">Launch: <code>#open:tier:id</code> (plugin) or <code>#view-x</code> (module), or a URL.</span>
            </label>
          </div>
          <div style="display:flex;gap:10px;">
            <label style="font-size:12px;color:#475569;flex:1;">Details link (popover page)
              <input id="adv-detailUrl" type="text" value="${esc(e.detailUrl || '')}" placeholder="/pages/pro/plugin-legal_k12_device_policy.html" style="width:100%;margin-top:3px;">
              <span style="font-size:10px;color:#94a3b8;">The item's listing page, opened in an in-app popover. Leave blank for no Details button.</span>
            </label>
          </div>
          <div style="display:flex;gap:10px;">
            <label style="font-size:12px;color:#475569;flex:1;">Domain
              <select id="adv-domain" style="width:100%;margin-top:3px;">${domainOptions(e.domain || '')}</select>
            </label>
            <label style="font-size:12px;color:#475569;flex:1;">Kind
              <select id="adv-kind" style="width:100%;margin-top:3px;">${kindOptions(e.kind)}</select>
            </label>
            <label style="font-size:12px;color:#475569;width:80px;">Order
              <input id="adv-order" type="number" value="${Number(e.order) || 0}" style="width:100%;margin-top:3px;">
            </label>
          </div>
          <div style="display:flex;gap:10px;">
            <label style="font-size:12px;color:#475569;flex:1;">Start (optional)
              <input id="adv-startAt" type="datetime-local" value="${esc(isoToLocalInput(e.startAt))}" style="width:100%;margin-top:3px;">
            </label>
            <label style="font-size:12px;color:#475569;flex:1;">End (optional)
              <input id="adv-endAt" type="datetime-local" value="${esc(isoToLocalInput(e.endAt))}" style="width:100%;margin-top:3px;">
            </label>
          </div>
          <div id="adv-error" style="color:#dc2626;font-size:12px;min-height:14px;"></div>
          <button type="submit" class="btn btn-sm btn-primary" style="align-self:flex-start;"><i class="fas fa-save"></i> ${editing ? 'Save changes' : 'Create advert'}</button>
        </form>

        <div style="margin-top:16px;">
          <div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:8px;">Dashboard preview</div>
          <div id="adv-preview"></div>
        </div>
      </div>
    </div>`;

  wire();
  renderPreview();
}

function wire() {
  const el = root(); if (!el) return;

  // Live preview: any form input refreshes the card.
  ['adv-title', 'adv-blurb', 'adv-icon', 'adv-accent', 'adv-ctaLabel', 'adv-ctaUrl', 'adv-detailUrl'].forEach(id => {
    el.querySelector('#' + id)?.addEventListener('input', renderPreview);
  });

  el.querySelector('#adv-cancel')?.addEventListener('click', () => { editing = null; render(); });

  el.querySelector('#adv-form')?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const errBox = el.querySelector('#adv-error');
    if (errBox) errBox.textContent = '';
    const g = id => (el.querySelector('#' + id)?.value || '').trim();
    const advert = {
      title: g('adv-title'),
      blurb: g('adv-blurb'),
      icon: g('adv-icon'),
      accent: g('adv-accent') || DEFAULT_ACCENT,
      ctaLabel: g('adv-ctaLabel'),
      ctaUrl: g('adv-ctaUrl'),
      detailUrl: g('adv-detailUrl'),
      domain: el.querySelector('#adv-domain')?.value || '',
      kind: el.querySelector('#adv-kind')?.value || 'feature',
      order: parseInt(g('adv-order'), 10) || 0,
      startAt: localInputToIso(g('adv-startAt')),
      endAt: localInputToIso(g('adv-endAt')),
    };
    if (editing && editing.id) advert.id = editing.id;   // include id -> upsert updates
    if (!advert.title) { if (errBox) errBox.textContent = 'Title is required.'; return; }
    try {
      await api('POST', '/api/admin/adverts', advert);
      editing = null;
      await load();
    } catch (err) {
      if (errBox) errBox.textContent = err.message || 'Save failed';
    }
  });

  el.querySelectorAll('.adv-edit').forEach(b => b.addEventListener('click', () => {
    const id = b.getAttribute('data-id');
    const row = rows.find(r => String(r.id) === String(id));
    if (!row) return;
    editing = row;
    render();
  }));

  el.querySelectorAll('.adv-del').forEach(b => b.addEventListener('click', async () => {
    const id = b.getAttribute('data-id');
    if (!confirm('Delete this advert? This cannot be undone.')) return;
    try {
      await api('DELETE', '/api/admin/adverts/' + encodeURIComponent(id));
      if (editing && String(editing.id) === String(id)) editing = null;
      await load();
    } catch (err) { alert(err.message || 'Delete failed'); }
  }));

  el.querySelectorAll('.adv-featured').forEach(cb => cb.addEventListener('change', async () => {
    const id = cb.getAttribute('data-id');
    const featured = cb.checked;
    try {
      await api('POST', '/api/admin/adverts/' + encodeURIComponent(id) + '/featured', { featured });
      const row = rows.find(r => String(r.id) === String(id));
      if (row) row.featured = featured;   // keep local state in sync without a full reload
    } catch (err) {
      cb.checked = !featured;             // revert on failure
      alert(err.message || 'Could not update featured state');
    }
  }));
}

async function load() {
  if (!isAdmin()) return;
  const el = root(); if (!el) return;
  try {
    if (!domains.length) await loadDomains();
    const res = await api('GET', '/api/admin/adverts');
    rows = res.data || [];
    // Sort by order asc so the operator sees them in dashboard display order.
    rows.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    loaded = true;
    render();
  } catch (err) {
    el.innerHTML = `<p style="color:#dc2626;">Could not load adverts: ${esc(err.message)}</p>`;
  }
}

// container is optional: when omitted we resolve #adverts-admin (the div inside
// #apanel-adverts). Lazy-loads on first Adverts tab open, mirroring
// initAdminSubscribers.
export function initAdminAdverts(container) {
  containerEl = container || document.getElementById('adverts-admin');
  const tab = document.querySelector('.atab[data-atab="adverts"]');
  if (!tab) return;
  tab.addEventListener('click', () => { if (!loaded) load(); else render(); });
}

export default { initAdminAdverts };
